/**
 * S01 — Cold Open
 *
 * Hero: MEYHEM LAUREN — stacked two-line display type that SHATTERS INTO
 * PLACE on mount. Each letter starts scattered (random rotation + blur +
 * translation) and assembles into the final title via a staggered GSAP
 * timeline. Behind the title: the HF#1 bloodied knife as atmospheric
 * backdrop. Underneath: "THE WHITE TRUFFLE ERA" with a hairline rule
 * above and below.
 *
 * Scroll behavior: title tracks out + fades slightly as user scrolls into
 * S02. Knife parallaxes upward subtly.
 */

import { useEffect, useRef, useMemo } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MonumentType } from "@/components/primitives/MonumentType";
import { sectionCopy } from "@/lib/voice";
import knifeSrc from "@/assets/higgsfield/hf-01-knife-coldopen.webp";

gsap.registerPlugin(ScrollTrigger);

/** Deterministic pseudo-random for consistent shatter pattern across renders */
function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

export function S01_ColdOpen() {
  const copy = sectionCopy.s01;
  const sectionRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLDivElement | null>(null);
  const knifeRef = useRef<HTMLDivElement | null>(null);
  const subtitleRef = useRef<HTMLDivElement | null>(null);
  const metaRef = useRef<HTMLDivElement | null>(null);
  const scrollHintRef = useRef<HTMLDivElement | null>(null);

  // Split hero into words → characters, wrapped in spans for per-letter animation.
  // We generate shatter offsets deterministically so SSR/hydration are consistent.
  const letterSpans = useMemo(() => {
    const words = copy.hero.split(" ");
    const rand = seededRandom(42);
    return words.map((word, wi) => ({
      word,
      chars: word.split("").map((ch, ci) => ({
        ch,
        // Each letter starts at a random offset from its final position
        dx: (rand() - 0.5) * 800,
        dy: (rand() - 0.5) * 500 - 100,
        rot: (rand() - 0.5) * 120,
        rotX: (rand() - 0.5) * 140,
        rotY: (rand() - 0.5) * 140,
        delay: rand() * 0.15,
        key: `${wi}-${ci}`,
      })),
    }));
  }, [copy.hero]);

  useEffect(() => {
    const el = sectionRef.current;
    const title = titleRef.current;
    const knife = knifeRef.current;
    const subtitle = subtitleRef.current;
    const meta = metaRef.current;
    const scrollHint = scrollHintRef.current;
    if (!el || !title || !knife) return;

    const ctx = gsap.context(() => {
      // Initial state — everything hidden
      gsap.set(meta, { opacity: 0, y: 20 });
      gsap.set(subtitle, { opacity: 0, y: 20 });
      gsap.set(scrollHint, { opacity: 0 });
      gsap.set(knife, { opacity: 0, scale: 1.15, filter: "blur(18px)" });

      // Letter shatter — each <span> starts scattered
      const letters = title.querySelectorAll<HTMLSpanElement>(".s01-letter");
      letters.forEach((letter) => {
        const dx = parseFloat(letter.dataset.dx || "0");
        const dy = parseFloat(letter.dataset.dy || "0");
        const rot = parseFloat(letter.dataset.rot || "0");
        const rotX = parseFloat(letter.dataset.rotx || "0");
        const rotY = parseFloat(letter.dataset.roty || "0");
        gsap.set(letter, {
          x: dx,
          y: dy,
          rotation: rot,
          rotationX: rotX,
          rotationY: rotY,
          opacity: 0,
          filter: "blur(24px)",
          transformOrigin: "50% 50%",
          transformStyle: "preserve-3d",
        });
      });

      // Master reveal timeline
      const tl = gsap.timeline({ delay: 0.3 });

      // 1. Knife fades in from blur
      tl.to(
        knife,
        {
          opacity: 0.72,
          scale: 1,
          filter: "blur(0px)",
          duration: 1.8,
          ease: "power2.out",
        },
        0,
      );

      // 2. Meta label
      tl.to(
        meta,
        { opacity: 0.7, y: 0, duration: 0.8, ease: "power2.out" },
        0.4,
      );

      // 3. Letters shatter INTO place — stagger + per-letter delay
      tl.to(
        letters,
        {
          x: 0,
          y: 0,
          rotation: 0,
          rotationX: 0,
          rotationY: 0,
          opacity: 1,
          filter: "blur(0px)",
          duration: 1.4,
          ease: "power4.out",
          stagger: {
            each: 0.035,
            from: "random",
          },
        },
        0.6,
      );

      // 4. Subtitle + hairline underline
      tl.to(
        subtitle,
        { opacity: 1, y: 0, duration: 1, ease: "power2.out" },
        "-=0.4",
      );

      // Animate the hairline rules under the subtitle
      tl.fromTo(
        [".s01-rule-top", ".s01-rule-bottom"],
        { scaleX: 0 },
        { scaleX: 1, duration: 1.2, ease: "expo.inOut", stagger: 0.15 },
        "-=0.7",
      );

      // 5. Scroll hint fades in last
      tl.to(
        scrollHint,
        { opacity: 0.5, duration: 0.8, ease: "power1.inOut" },
        "-=0.3",
      );

      // Scroll-driven: title tracks out as user scrolls
      gsap.to(title, {
        opacity: 0.15,
        letterSpacing: "0.12em",
        scale: 1.05,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
      gsap.to(knife, {
        y: -40,
        scale: 1.08,
        ease: "none",
        scrollTrigger: {
          trigger: el,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
    }, el);

    return () => ctx.revert();
  }, [copy.hero]);

  return (
    <section
      ref={sectionRef}
      id="s01"
      className="section"
      style={{
        backgroundColor: "var(--ink)",
        color: "var(--bone)",
        minHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Background vignette */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at center, rgba(25,15,10,0.6) 0%, rgba(5,5,5,0.95) 100%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <MonumentType word={copy.monument} theme="ink" />

      {/* Knife — atmospheric backdrop */}
      <div
        ref={knifeRef}
        style={{
          position: "absolute",
          top: "58%",
          left: "50%",
          width: "min(900px, 75vw)",
          height: "auto",
          transform: "translate(-50%, -40%)",
          zIndex: 1,
          pointerEvents: "none",
          willChange: "transform, filter, opacity",
        }}
      >
        <img
          src={knifeSrc}
          alt=""
          style={{
            width: "100%",
            height: "auto",
            objectFit: "contain",
            filter:
              "drop-shadow(0 30px 80px rgba(0,0,0,0.95)) brightness(0.88) contrast(1.1)",
          }}
        />
      </div>

      {/* Hero type stack */}
      <div
        ref={titleRef}
        style={{
          position: "relative",
          textAlign: "center",
          zIndex: 2,
          pointerEvents: "none",
          maxWidth: "94vw",
          margin: "0 auto",
          perspective: "1200px",
        }}
      >
        <h1
          style={{
            color: "var(--bone)",
            margin: 0,
            textShadow: "0 4px 60px rgba(0,0,0,0.92)",
            fontFamily: "var(--f-serif-display)",
            fontWeight: 900,
            fontStretch: "75%",
            fontSize: "clamp(4rem, 18vw, 16rem)",
            lineHeight: 0.82,
            letterSpacing: "-0.035em",
            textTransform: "uppercase",
            willChange: "transform, filter, opacity, letter-spacing",
          }}
        >
          {letterSpans.map(({ chars }, wi) => (
            <span
              key={wi}
              style={{
                display: "block",
                whiteSpace: "nowrap",
              }}
            >
              {chars.map((c) => (
                <span
                  key={c.key}
                  className="s01-letter"
                  data-dx={c.dx}
                  data-dy={c.dy}
                  data-rot={c.rot}
                  data-rotx={c.rotX}
                  data-roty={c.rotY}
                  style={{
                    display: "inline-block",
                    willChange: "transform, filter, opacity",
                  }}
                >
                  {c.ch}
                </span>
              ))}
              {/* trailing space stays in layout but not animated */}
              {wi < letterSpans.length - 1 && " "}
            </span>
          ))}
        </h1>

        {/* Subtitle — hairline-ruled editorial */}
        {copy.subhero && (
          <div
            ref={subtitleRef}
            style={{
              position: "relative",
              marginTop: "var(--space-lg)",
              paddingTop: "var(--space-md)",
              paddingBottom: "var(--space-md)",
              display: "inline-block",
              willChange: "transform, opacity",
            }}
          >
            <div
              className="s01-rule-top"
              aria-hidden="true"
              style={{
                position: "absolute",
                top: 0,
                left: "50%",
                width: "100%",
                maxWidth: "32rem",
                height: "1px",
                background: "var(--gold)",
                transform: "translateX(-50%) scaleX(0)",
                transformOrigin: "center",
                opacity: 0.85,
              }}
            />
            <div
              className="type-meta-lg"
              style={{
                color: "var(--gold)",
                letterSpacing: "var(--ls-meta-extra)",
                fontSize: "clamp(0.75rem, 1vw, 1.125rem)",
                textShadow: "0 2px 20px rgba(0,0,0,0.9)",
                fontWeight: 400,
              }}
            >
              {copy.subhero}
            </div>
            <div
              className="s01-rule-bottom"
              aria-hidden="true"
              style={{
                position: "absolute",
                bottom: 0,
                left: "50%",
                width: "100%",
                maxWidth: "32rem",
                height: "1px",
                background: "var(--gold)",
                transform: "translateX(-50%) scaleX(0)",
                transformOrigin: "center",
                opacity: 0.85,
              }}
            />
          </div>
        )}
      </div>

      {/* Meta label */}
      <div
        ref={metaRef}
        className="type-meta-sm"
        style={{
          position: "absolute",
          top: "14vh",
          left: "50%",
          transform: "translateX(-50%)",
          color: "var(--fog)",
          letterSpacing: "var(--ls-meta-wide)",
          zIndex: 3,
          willChange: "transform, opacity",
        }}
      >
        {copy.meta}
      </div>

      {/* Scroll indicator */}
      <div
        ref={scrollHintRef}
        className="type-meta-sm"
        style={{
          position: "absolute",
          bottom: "15vh",
          left: "50%",
          transform: "translateX(-50%)",
          color: "var(--fog)",
          zIndex: 3,
          letterSpacing: "var(--ls-meta-wide)",
        }}
      >
        ↓ SCROLL
      </div>
    </section>
  );
}
